import { track } from '@/common/track';
import { AppCardSelect } from '@/components/AppCardSelect';
import { BaseSelectMenuOption } from '@/types/components/selector.types';
import { Stack, Typography } from '@mui/material';
import { CourseEvent, Theme } from 'klayowebshared';
import { CheckoutBody } from '@/hooks/useCheckoutCourse';
import { useEffect, useState } from 'react';
import { Dialog } from '../../components/dialogs/Dialog';
import { useLocale } from '../../providers/LocaleProvider';

type PurchaseFor = 'myself' | 'my-team';

const PURCHASE_FOR_OPTIONS: BaseSelectMenuOption[] = [
  { value: 'myself', label: 'myKlayo.myself' },
  { value: 'my-team', label: 'myKlayo.myTeam' },
];

const TEAM_SIZE_OPTIONS: BaseSelectMenuOption[] = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '10+',
].map((n) => ({ value: n, label: n }));

type Props = {
  open: boolean;
  theme: typeof Theme;
  courseName?: string;
  onClose: () => void;
  onPurchase: (body: CheckoutBody) => void;
  isLoading: boolean;
};

export const PurchaseOptionsDialog = ({
  open,
  theme,
  courseName,
  onClose,
  onPurchase,
  isLoading,
}: Props) => {
  const { translate } = useLocale();
  const [purchaseFor, setPurchaseFor] = useState<PurchaseFor>('myself');
  const [teamSize, setTeamSize] = useState<string | null>(null);

  useEffect(() => {
    if (open) track(CourseEvent.ViewedMultiplePurchaseOptions);
  }, [open]);

  const handleClose = () => {
    track(CourseEvent.ClickBuyCancel, { courseName });
    setPurchaseFor('myself');
    setTeamSize(null);
    onClose();
  };

  const handleContinue = () => {
    track(CourseEvent.ClickBuyCheckout, { courseName });
    if (purchaseFor === 'myself') {
      onPurchase({ quantity: 1, forMyself: true });
    } else {
      const count = teamSize === '10+' ? 10 : parseInt(teamSize!, 10);
      onPurchase({ quantity: count, forMyself: false });
    }
  };

  const canContinue = purchaseFor === 'myself' || teamSize !== null;

  const dialogActions = [
    {
      label: translate('myKlayo.continueToCheckout'),
      onClick: handleContinue,
      variant: 'filled',
      disabled: !canContinue || isLoading,
      isLoading,
    },
    {
      label: translate('common.cancel'),
      onClick: handleClose,
      variant: 'outlined',
    },
  ];

  return (
    <Dialog
      open={open}
      theme={theme}
      title={translate('myKlayo.buyCourse')}
      onClose={handleClose}
      maxWidth='sm'
      actions={dialogActions}
    >
      <Stack sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Stack gap={2}>
          <Typography variant='body2'>{translate('myKlayo.whoAreYouBuyingFor')}</Typography>
          <AppCardSelect
            items={PURCHASE_FOR_OPTIONS}
            value={purchaseFor}
            onChange={(value) => {
              if (value !== null) {
                setPurchaseFor(value as PurchaseFor);
                setTeamSize(null);
                track(value === 'myself' ? CourseEvent.ClickBuyMyself : CourseEvent.ClickBuyTeam, {
                  courseName,
                });
              }
            }}
          />
        </Stack>

        {purchaseFor === 'my-team' && (
          <Stack gap={2}>
            <Typography variant='body2'>{translate('myKlayo.howManyPeopleInYourTeam')}</Typography>
            <AppCardSelect
              items={TEAM_SIZE_OPTIONS}
              value={teamSize}
              onChange={(value) => {
                if (value !== null) {
                  setTeamSize(value);
                  track(CourseEvent.ClickBuyTeamQty, { courseName, qty: value });
                }
              }}
            />
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
};
